import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { POLYGON_COMPLETE, POLYGON_TYPE } from 'store/actions';
import { IconButton, ToggleButton, ToggleButtonGroup, Snackbar, Alert } from '@mui/material';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseIcon from '@mui/icons-material/Close';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import PanToolIcon from '@mui/icons-material/PanTool';
// import DeleteIcon from '@mui/icons-material/Delete';
import defaultImage from '../../../../../assets/images/dfmd.png';
import { polygonColors } from './MiddleController';

const ImageAnnotationTool = ({ handleCloseAnnotate }) => {
  const [drawingMode, setDrawingMode] = useState('polygon');
  const [drawingData, setDrawingData] = useState([]);
  const [startPoint, setStartPoint] = useState(null);
  const [mousePos, setMousePos] = useState(null);
  const [scale, setScale] = useState(1);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error'
  });

  const imageRef = useRef(null);

  const dispatch = useDispatch();
  const currentPolygonType = useSelector((state) => state.cameraReducer.polygonType);
  const polygonData = useSelector((state) => state.cameraReducer.polygonData);
  const cameraImage = useSelector((state) => state.cameraReducer.cameraImage);
  const zoneNames = useSelector((state) => state.cameraReducer.zoneNames);
  const zoneColors = useSelector((state) => state.cameraReducer.zoneColors);
  const borderLineNames = useSelector((state) => state.cameraReducer.borderLineNames);
  const borderLineColors = useSelector((state) => state.cameraReducer.borderLineColors);

  const getPolygonColor = (type, index = null) => {
    if (type === 'zones') {
      const zoneName = index !== null ? zoneNames[index] : zoneNames[zoneNames.length - 1];
      return zoneColors[zoneName] || '#535BAC';
    } else if (type === 'frisking_line') {
      const borderLineName = index !== null ? borderLineNames[index] : borderLineNames[borderLineNames.length - 1];
      return borderLineColors[borderLineName] || '#0000FF';
    }
    return polygonColors[type] || '#535BAC';
  };

  const handleDrawingModeChange = (event, newMode) => {
    if (newMode !== null) {
      setDrawingMode(newMode);
    }
  };

  const handleMouseDown = (e) => {
    if (drawingMode !== 'polygon' || !currentPolygonType) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    if (!startPoint) {
      setStartPoint({ x, y });
    } else {
      const newPoint = { x, y };

      // Check if we're trying to close a zone
      if (currentPolygonType === 'zones' && drawingData.length >= 2) {
        const firstPoint = drawingData[0].start;
        const distance = Math.sqrt(Math.pow(x - firstPoint.x, 2) + Math.pow(y - firstPoint.y, 2));

        if (distance < 20) {
          handleDone();
          return;
        }
      }

      setDrawingData([
        ...drawingData,
        {
          start: startPoint,
          end: newPoint
        }
      ]);

      // For border lines, complete after 2 points
      if (currentPolygonType === 'frisking_line' && drawingData.length >= 1) {
        handleDone();
        return;
      }

      setStartPoint(newPoint);
    }
  };

  const handleMouseMove = (e) => {
    if (!startPoint || drawingMode !== 'polygon' || !currentPolygonType) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    setMousePos({ x, y });
  };

  const handleMouseUp = () => {
    // Remove automatic completion on mouse up
    // Let handleMouseDown handle the completion
  };

  const handleUndo = () => {
    if (drawingData.length > 0) {
      setDrawingData((prevData) => prevData.slice(0, -1));
      if (drawingData.length > 1) {
        setStartPoint({
          x: drawingData[drawingData.length - 2].end.x,
          y: drawingData[drawingData.length - 2].end.y
        });
      } else {
        setStartPoint(null);
      }
    }
  };

  const handleDone = () => {
    // For zones, we need 3 points (2 lines + closing line)
    if (currentPolygonType === 'zones' && drawingData.length < 2) {
      setSnackbar({
        open: true,
        message: 'A zone requires at least 3 points',
        severity: 'error'
      });
      return;
    }

    // For border lines, we need 2 points (1 line)
    if (currentPolygonType === 'frisking_line' && drawingData.length < 1) {
      setSnackbar({
        open: true,
        message: 'A border line requires at least 2 points',
        severity: 'error'
      });
      return;
    }

    let coordinates = [];

    if (currentPolygonType === 'frisking_line') {
      coordinates = [
        ...drawingData.map((line) => [line.start.x, line.start.y]).flat(),
        drawingData[drawingData.length - 1].end.x,
        drawingData[drawingData.length - 1].end.y
      ];
    } else {
      // For zones, close the polygon by connecting last point to first point
      coordinates = [
        ...drawingData.map((line) => [line.start.x, line.start.y]).flat(),
        drawingData[drawingData.length - 1].end.x,
        drawingData[drawingData.length - 1].end.y,
        drawingData[0].start.x,
        drawingData[0].start.y
      ];
    }

    dispatch({
      type: POLYGON_COMPLETE,
      payload: {
        type: currentPolygonType,
        coordinates
      }
    });

    // Reset drawing state
    setDrawingData([]);
    setStartPoint(null);
    dispatch({ type: POLYGON_TYPE, polygonType: null });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // const handleError = (error) => {
  //   const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
  //   setSnackbar({
  //     open: true,
  //     message: errorMessage,
  //     severity: 'error'
  //   });
  // };

  // const handleDeletePolygon = (type, index) => {
  //   // Implement polygon deletion logic here
  // };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Top-left: Toggle Buttons for modes */}
      <div
        style={{
          position: 'fixed',
          top: 20,
          left: 20,
          zIndex: 1000,
          display: 'flex',
          gap: '10px'
        }}
      >
        <ToggleButtonGroup
          value={drawingMode}
          exclusive
          onChange={handleDrawingModeChange}
          aria-label="drawing mode"
          style={{
            backgroundColor: 'white',
            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          <ToggleButton value="polygon" aria-label="polygon mode">
            <TimelineOutlinedIcon />
          </ToggleButton>
          <ToggleButton value="drag" aria-label="drag mode">
            <PanToolIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <TransformWrapper
        initialScale={1}
        disabled={drawingMode === 'polygon'}
        onZoom={({ state }) => setScale(state.scale)}
        wheel={{
          step: 0.1
        }}
        pan={{
          velocityEqualToMove: true
        }}
        pinch={{ disabled: true }}
      >
        <TransformComponent>
          <div
            style={{ position: 'relative' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            className={drawingMode === 'drag' ? 'cursor-grab active:cursor-grabbing' : 'cursor-crosshair'}
          >
            <img
              ref={imageRef}
              src={cameraImage || defaultImage}
              alt="Camera Feed"
              style={{
                width: '100%',
                height: 'auto',
                userSelect: 'none',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.target.src = defaultImage; // Fallback to default image on error
              }}
            />
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
              }}
            >
              {/* Drawing lines */}
              {drawingData.map((line, index) => (
                <line
                  key={index}
                  x1={line.start.x}
                  y1={line.start.y}
                  x2={line.end.x}
                  y2={line.end.y}
                  stroke={getPolygonColor(currentPolygonType)}
                  strokeWidth={`${2 / scale}`}
                  vectorEffect="non-scaling-stroke"
                />
              ))}

              {/* Preview line */}
              {startPoint && mousePos && (
                <line
                  x1={startPoint.x}
                  y1={startPoint.y}
                  x2={mousePos.x}
                  y2={mousePos.y}
                  stroke={getPolygonColor(currentPolygonType)}
                  strokeWidth={`${2 / scale}`}
                  strokeDasharray="5,5"
                  vectorEffect="non-scaling-stroke"
                />
              )}

              {startPoint && (
                <circle
                  cx={startPoint.x}
                  cy={startPoint.y}
                  r={4 / scale}
                  fill={getPolygonColor(currentPolygonType)}
                  vectorEffect="non-scaling-stroke"
                />
              )}

              {/* Completed polygons */}
              {polygonData &&
                Object.entries(polygonData).map(([type, typePolygons]) =>
                  Object.entries(typePolygons || {}).map(([name, points]) => {
                    if (!points || points.length === 0) return null;

                    const pointPairs = points.reduce((pairs, point, i) => {
                      if (i % 2 === 0) {
                        pairs.push(`${point},${points[i + 1]}`);
                      }
                      return pairs;
                    }, []);

                    const color = type === 'zones' ? zoneColors[name] : borderLineColors[name];

                    if (type === 'frisking_line') {
                      return (
                        <polyline
                          key={`${type}-${name}`}
                          points={pointPairs.join(' ')}
                          fill="none"
                          stroke={color}
                          strokeWidth={`${2 / scale}`}
                          vectorEffect="non-scaling-stroke"
                        />
                      );
                    }

                    return (
                      <polyline
                        key={`${type}-${name}`}
                        points={pointPairs.join(' ')}
                        fill={`${color}40`}
                        stroke={color}
                        strokeWidth={`${2 / scale}`}
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })
                )}
            </svg>
          </div>
        </TransformComponent>
      </TransformWrapper>

      {/* Top-right: Undo and Done buttons */}
      <div
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 1000,
          display: 'flex',
          gap: '10px'
        }}
      >
        <IconButton
          onClick={handleUndo}
          disabled={drawingMode === 'drag'}
          style={{
            backgroundColor: 'white',
            color: drawingMode === 'drag' ? 'rgba(0, 0, 0, 0.26)' : 'black',
            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          <KeyboardReturnRoundedIcon />
        </IconButton>
        <IconButton
          onClick={handleDone}
          disabled={drawingMode === 'drag'}
          style={{
            backgroundColor: 'white',
            color: drawingMode === 'drag' ? 'rgba(0, 0, 0, 0.26)' : 'black',
            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          <CheckRoundedIcon />
        </IconButton>
        <IconButton
          onClick={handleCloseAnnotate}
          style={{
            backgroundColor: 'white',
            color: 'black',
            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          <CloseIcon />
        </IconButton>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ImageAnnotationTool;
